import React, { useEffect, useState, useMemo } from "react";
import api, { API } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, FileDown, Search, BookOpen, ChevronRight, ExternalLink, Image as ImageIcon, Sparkles, LifeBuoy, Upload, RefreshCw, PlayCircle, Share2, Copy, CheckCircle2, Camera, Video } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function UserGuide() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [activeAnchor, setActiveAnchor] = useState("");
  const [genBusy, setGenBusy] = useState({});
  const [batchQueued, setBatchQueued] = useState(0);
  const [progress, setProgress] = useState({ total: 0, done: 0, failed: 0, references_uploaded: 0 });
  const [wt, setWt] = useState({ completed: [], in_progress: {} });
  const [captureBusy, setCaptureBusy] = useState(false);
  const [videoBusy, setVideoBusy] = useState(false);
  const [videoProgress, setVideoProgress] = useState({ total: 0, done: 0, failed: 0, codes_ready: [] });
  const [videoRecording, setVideoRecording] = useState({});   // per-feature busy

  const reload = async () => {
    try {
      const [g, p, w, v] = await Promise.all([
        api.get("/user-guide"),
        api.get("/user-guide/image-status").catch(() => ({ data: { total: 0, done: 0, failed: 0, references_uploaded: 0 } })),
        api.get("/user-guide/walkthroughs").catch(() => ({ data: { completed: [], in_progress: {} } })),
        api.get("/user-guide/video-status").catch(() => ({ data: { total: 0, done: 0, failed: 0, codes_ready: [] } })),
      ]);
      setData(g.data);
      setProgress(p.data);
      setWt(w.data);
      setVideoProgress(v.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load guide"); }
  };
  useEffect(() => { reload(); }, []);
  // Poll every 8s while a batch is running so thumbnails + progress bar update live
  useEffect(() => {
    if (batchQueued <= 0) return;
    const iv = setInterval(async () => {
      await reload();
      // Auto-stop polling once done + failed >= queued+already_done
      const p = await api.get("/user-guide/image-status").then((r) => r.data).catch(() => null);
      if (p && (p.done + p.failed) >= p.total) setBatchQueued(0);
    }, 8000);
    return () => clearInterval(iv);
  }, [batchQueued]);

  const uploadRef = async (feature_code, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/user-guide/reference/${feature_code}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Reference uploaded — click Generate to use it");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  // Iter-111 · Direct real-screenshot upload (bypasses AI, used as-is)
  const uploadScreenshot = async (feature_code, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/user-guide/upload-screenshot/${feature_code}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Real screenshot uploaded — used as-is (no AI)");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const openTicket = (feat) => {
    navigate(`/app/tickets?prefill_title=${encodeURIComponent("Help with " + feat.name)}&prefill_body=${encodeURIComponent(`I need help with the "${feat.name}" feature (${feat.where}). Describe your question here…`)}`);
  };

  // Iter-110 · Launch AI walkthrough on the feature's actual page
  const startWalkthrough = (feat) => {
    const sep = feat.where.includes("?") ? "&" : "?";
    navigate(`${feat.where}${sep}walkthrough=${feat.code}`);
  };

  // Iter-110 · Generate a signed share link
  const shareFeature = async (feat) => {
    try {
      const r = await api.post(`/user-guide/share/${feat.code}`);
      const origin = window.location.origin;
      const url = origin + r.data.share_url;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied · valid 30 days");
      } catch {
        window.prompt("Copy this share link (valid 30 days):", url);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create share link"); }
  };

  const generateOne = async (feature_code) => {
    setGenBusy({ ...genBusy, [feature_code]: true });
    try {
      await api.post(`/user-guide/generate-image/${feature_code}?force=true`);
      toast.success("Screenshot generated");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to generate"); }
    setGenBusy({ ...genBusy, [feature_code]: false });
  };

  const generateAll = async () => {
    try {
      const r = await api.post("/user-guide/generate-all");
      setBatchQueued(r.data.queued || 0);
      toast.success(`Queued ${r.data.queued} screenshots · Already done: ${r.data.already_done}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to queue"); }
  };

  // Iter-112 · Kick off real-UI capture via Playwright on the backend
  const captureLiveAll = async () => {
    setCaptureBusy(true);
    try {
      const r = await api.post("/user-guide/capture-live-all");
      setBatchQueued(r.data.queued || 0);
      toast.success(`Capturing ${r.data.queued} live screenshots (real UI) · already done: ${r.data.already_real_captured || 0}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Live capture failed");
    }
    setCaptureBusy(false);
  };

  // Iter-113 · Record ~25-sec walkthrough video for every feature (bulk)
  const recordAllVideos = async () => {
    setVideoBusy(true);
    try {
      const r = await api.post("/user-guide/record-video-all");
      toast.success(`Recording ${r.data.queued} walkthrough videos (real UI) · ETA ~${r.data.eta_minutes} min · already done: ${r.data.already_recorded}`);
      // Poll video-status every 15s
      const iv = setInterval(async () => {
        try {
          const s = await api.get("/user-guide/video-status");
          setVideoProgress(s.data);
          if (s.data.done + s.data.failed >= s.data.total) {
            clearInterval(iv);
            setVideoBusy(false);
            toast.success(`Videos ready: ${s.data.done}/${s.data.total}`);
          }
        } catch { clearInterval(iv); setVideoBusy(false); }
      }, 15000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Video recording failed");
      setVideoBusy(false);
    }
  };

  const recordOneVideo = async (feature_code) => {
    setVideoRecording((s) => ({ ...s, [feature_code]: true }));
    try {
      await api.post(`/user-guide/record-video/${feature_code}`);
      toast.success("Walkthrough video recorded");
      // Refresh video-status only (fast path)
      const s = await api.get("/user-guide/video-status");
      setVideoProgress(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recording failed");
    }
    setVideoRecording((s) => ({ ...s, [feature_code]: false }));
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!query.trim()) return data.sections;
    const q = query.toLowerCase();
    return data.sections
      .map((s) => ({
        ...s,
        features: s.features.filter((f) =>
          f.name.toLowerCase().includes(q) ||
          f.meaning.toLowerCase().includes(q) ||
          (f.usage || "").toLowerCase().includes(q)
        ),
      }))
      .filter((s) => s.features.length > 0);
  }, [data, query]);

  const download = (fmt) => {
    const a = document.createElement("a");
    a.href = `${API}/user-guide/download?fmt=${fmt}`;
    a.target = "_blank"; a.rel = "noopener";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); setActiveAnchor(id); }
  };

  if (!data) return <div className="p-8 text-muted-foreground">Loading user guide…</div>;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="user-guide-page">
      <PageHeader eyebrow="Documentation" title="Zenith 360 · Feature Guide"
                  description={`${data.summary.sections} modules · ${data.summary.features} features · click any entry in the sidebar to jump to it.`} />

      {/* Iter-109 · Batch generation progress bar */}
      {progress.total > 0 && (
        <div className="rounded-sm border border-border bg-muted/20 p-3" data-testid="guide-progress">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-1.5">
              {batchQueued > 0 && <RefreshCw className="w-3 h-3 animate-spin" />}
              // Screenshots: {progress.done} / {progress.total} ready
              {progress.failed > 0 && <span className="text-red-700"> · {progress.failed} failed</span>}
              {progress.references_uploaded > 0 && <span className="text-blue-700"> · {progress.references_uploaded} references uploaded</span>}
            </div>
            <div className="text-[10px] font-mono text-muted-foreground">{Math.round((progress.done / progress.total) * 100)}%</div>
          </div>
          <div className="w-full h-1.5 rounded-full bg-border/40 overflow-hidden">
            <div className="h-full bg-accent transition-all duration-500" style={{ width: `${(progress.done / progress.total) * 100}%` }} data-testid="guide-progress-bar" />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[260px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
                   placeholder="Search features…" className="rounded-sm pl-9" data-testid="guide-search" />
        </div>
        <Button size="sm" onClick={captureLiveAll} disabled={captureBusy} className="rounded-sm gap-1 bg-accent hover:bg-accent/90 text-white" data-testid="guide-capture-all">
          {captureBusy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
          Capture live UI (real screenshots)
        </Button>
        <Button size="sm" onClick={recordAllVideos} disabled={videoBusy} className="rounded-sm gap-1 bg-red-600 hover:bg-red-700 text-white" data-testid="guide-video-all"
                  title="Record a ~25-sec walkthrough video for every feature — real UI, scripted scroll + hover.">
          {videoBusy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Video className="w-3.5 h-3.5" />}
          Record videos ({videoProgress.done}/{videoProgress.total})
        </Button>
        <Button size="sm" variant="outline" onClick={generateAll} className="rounded-sm gap-1" data-testid="guide-gen-all"
                  title="Fallback: AI-drawn illustration via Gemini Nano Banana. Use only if live capture fails.">
          <Sparkles className="w-3.5 h-3.5" /> AI illustration (fallback)
        </Button>
        <Button size="sm" variant="outline" onClick={() => download("pdf")} className="rounded-sm gap-1" data-testid="guide-download-pdf">
          <FileDown className="w-3.5 h-3.5" /> PDF
        </Button>
        <Button size="sm" variant="outline" onClick={() => download("docx")} className="rounded-sm gap-1" data-testid="guide-download-docx">
          <FileText className="w-3.5 h-3.5" /> Word (.docx)
        </Button>
        <Button size="sm" onClick={() => download("xlsx")} className="rounded-sm gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="guide-download-xlsx">
          <FileDown className="w-3.5 h-3.5" /> Excel
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Sticky sidebar TOC */}
        <aside className="col-span-12 md:col-span-4 lg:col-span-3">
          <div className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2 border-r border-border" data-testid="guide-toc">
            <div className="text-[10px] font-mono uppercase text-muted-foreground mb-2 flex items-center gap-1">
              <BookOpen className="w-3 h-3" /> Contents
            </div>
            {filtered.map((section, i) => (
              <div key={section.code} className="mb-3">
                <button onClick={() => scrollTo(`sec-${section.code}`)}
                          className="text-left text-sm font-semibold text-foreground hover:text-accent transition-colors"
                          data-testid={`toc-sec-${section.code}`}>
                  {i + 1}. {section.title}
                </button>
                <div className="ml-2 mt-1 space-y-0.5">
                  {section.features.map((f, j) => {
                    const id = `feat-${section.code}-${f.code}`;
                    return (
                      <button key={f.code} onClick={() => scrollTo(id)}
                                className={`text-left w-full text-xs py-0.5 pl-2 border-l ${activeAnchor === id ? "border-accent text-accent font-medium" : "border-border text-muted-foreground hover:text-foreground"}`}
                                data-testid={`toc-feat-${f.code}`}>
                        {i + 1}.{j + 1} {f.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* Content */}
        <div className="col-span-12 md:col-span-8 lg:col-span-9 space-y-8">
          {filtered.map((section, i) => (
            <section key={section.code} id={`sec-${section.code}`} data-testid={`guide-sec-${section.code}`}>
              <div className="border-b-2 border-accent/40 pb-2 mb-4">
                <div className="text-[10px] font-mono uppercase text-muted-foreground">// module {i + 1}</div>
                <h2 className="text-2xl font-heading font-bold mt-1">{section.title}</h2>
                <p className="text-sm text-muted-foreground mt-1">{section.intro}</p>
              </div>

              <div className="space-y-4">
                {section.features.map((feat, j) => {
                  const id = `feat-${section.code}-${feat.code}`;
                  return (
                    <Card key={feat.code} id={id} className="rounded-sm p-5 scroll-mt-24" data-testid={`guide-feat-${feat.code}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <div className="text-[10px] font-mono uppercase text-muted-foreground">{i + 1}.{j + 1}</div>
                            {feat.changed_in && (
                              <span className="text-[10px] font-mono uppercase bg-accent/15 text-accent px-1.5 py-0.5 rounded-sm" data-testid={`guide-changed-${feat.code}`}>
                                Changed in {feat.changed_in}
                              </span>
                            )}
                          </div>
                          <h3 className="text-lg font-heading font-semibold mt-1">{feat.name}</h3>
                        </div>
                        <div className="flex items-center gap-3">
                          {wt.completed?.includes(feat.code) ? (
                            <span className="text-[10px] font-mono uppercase bg-emerald-500/15 text-emerald-800 px-1.5 py-0.5 rounded-sm flex items-center gap-1" data-testid={`guide-watched-${feat.code}`}>
                              <CheckCircle2 className="w-3 h-3" /> Watched
                            </span>
                          ) : wt.in_progress?.[feat.code] != null ? (
                            <button onClick={() => startWalkthrough(feat)}
                                      className="text-xs text-blue-700 hover:underline flex items-center gap-1 font-medium"
                                      data-testid={`guide-resume-${feat.code}`}>
                              <PlayCircle className="w-3 h-3" /> Resume (step {wt.in_progress[feat.code] + 1})
                            </button>
                          ) : (
                            <button onClick={() => startWalkthrough(feat)}
                                      className="text-xs text-emerald-700 hover:underline flex items-center gap-1"
                                      data-testid={`guide-walkthrough-${feat.code}`}>
                              <PlayCircle className="w-3 h-3" /> Watch me do it
                            </button>
                          )}
                          <button onClick={() => shareFeature(feat)}
                                    className="text-xs text-blue-700 hover:underline flex items-center gap-1"
                                    data-testid={`guide-share-${feat.code}`}>
                            <Share2 className="w-3 h-3" /> Share
                          </button>
                          <button onClick={() => openTicket(feat)}
                                    className="text-xs text-amber-700 hover:underline flex items-center gap-1"
                                    data-testid={`guide-help-${feat.code}`}>
                            <LifeBuoy className="w-3 h-3" /> Need help?
                          </button>
                          <a href={feat.where} className="text-xs text-accent hover:underline flex items-center gap-1" data-testid={`guide-open-${feat.code}`}>
                            <ExternalLink className="w-3 h-3" /> Open in app
                          </a>
                        </div>
                      </div>

                      {/* Screenshot / illustration + reference upload */}
                      <div className="mb-4">
                        {feat.has_image ? (
                          <img src={`${API}/user-guide/image/${feat.code}?ts=${Date.now()}`}
                                 alt={feat.name}
                                 className="w-full max-w-2xl rounded-sm border border-border shadow-sm"
                                 data-testid={`guide-img-${feat.code}`} />
                        ) : (
                          <div className="w-full max-w-2xl h-40 rounded-sm border border-dashed border-border bg-muted/30 flex items-center justify-center gap-3"
                                data-testid={`guide-noimg-${feat.code}`}>
                            <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
                            <div className="text-xs text-muted-foreground">No illustration yet</div>
                            <Button size="sm" variant="outline" onClick={() => generateOne(feat.code)}
                                      disabled={!!genBusy[feat.code]} className="rounded-sm gap-1"
                                      data-testid={`guide-gen-${feat.code}`}>
                              <Sparkles className="w-3 h-3" /> {genBusy[feat.code] ? "Generating…" : "Generate"}
                            </Button>
                          </div>
                        )}
                        {/* Iter-113 · Embedded walkthrough video */}
                        {videoProgress.codes_ready?.includes(feat.code) ? (
                          <video controls preload="none"
                                   poster={feat.has_image ? `${API}/user-guide/image/${feat.code}?ts=${Date.now()}` : undefined}
                                   className="w-full max-w-2xl rounded-sm border border-border shadow-sm mt-2 bg-black"
                                   data-testid={`guide-video-${feat.code}`}>
                            <source src={`${API}/user-guide/video/${feat.code}`} type="video/webm" />
                            Your browser doesn't support embedded video.
                          </video>
                        ) : (
                          <div className="mt-2">
                            <Button size="sm" variant="outline" onClick={() => recordOneVideo(feat.code)}
                                      disabled={!!videoRecording[feat.code] || videoBusy}
                                      className="rounded-sm gap-1"
                                      data-testid={`guide-video-record-${feat.code}`}>
                              {videoRecording[feat.code] ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Video className="w-3 h-3" />}
                              {videoRecording[feat.code] ? "Recording (~25s)…" : "Record walkthrough video"}
                            </Button>
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <label className="text-[10px] font-mono uppercase text-emerald-800 hover:text-emerald-900 cursor-pointer flex items-center gap-1 bg-emerald-500/10 px-2 py-1 rounded-sm" data-testid={`guide-real-upload-${feat.code}`}>
                            <Camera className="w-3 h-3" /> Upload real screenshot (used as-is)
                            <input type="file" accept="image/png,image/jpeg" className="hidden"
                                     onChange={(e) => uploadScreenshot(feat.code, e.target.files?.[0])} />
                          </label>
                          <label className="text-[10px] font-mono uppercase text-muted-foreground hover:text-accent cursor-pointer flex items-center gap-1" data-testid={`guide-ref-upload-${feat.code}`}>
                            <Upload className="w-3 h-3" /> Reference for AI
                            <input type="file" accept="image/png,image/jpeg" className="hidden"
                                     onChange={(e) => uploadRef(feat.code, e.target.files?.[0])} />
                          </label>
                          {feat.has_image && (
                            <button onClick={() => generateOne(feat.code)} className="text-[10px] font-mono uppercase text-accent hover:underline flex items-center gap-1" data-testid={`guide-regen-${feat.code}`}>
                              <RefreshCw className="w-3 h-3" /> Regenerate
                            </button>
                          )}
                        </div>
                      </div>

                      <dl className="grid md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                        <div>
                          <dt className="text-[10px] font-mono uppercase text-accent">Meaning</dt>
                          <dd className="mt-1">{feat.meaning}</dd>
                        </div>
                        <div>
                          <dt className="text-[10px] font-mono uppercase text-accent">Requirement</dt>
                          <dd className="mt-1">{feat.requirement}</dd>
                        </div>
                        <div className="md:col-span-2">
                          <dt className="text-[10px] font-mono uppercase text-accent">Usage</dt>
                          <dd className="mt-1">{feat.usage}</dd>
                        </div>
                      </dl>

                      <div className="mt-4">
                        <div className="text-[10px] font-mono uppercase text-accent mb-2 flex items-center gap-1">
                          <ChevronRight className="w-3 h-3" /> Process steps
                        </div>
                        <ol className="list-decimal ml-5 space-y-1 text-sm">
                          {feat.steps.map((s, k) => <li key={k}>{s}</li>)}
                        </ol>
                      </div>

                      <div className="mt-3 pt-3 border-t border-dashed border-border text-[10px] font-mono text-muted-foreground">
                        // where · <span className="font-medium text-foreground">{feat.where}</span>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </section>
          ))}

          {filtered.length === 0 && (
            <Card className="rounded-sm p-8 text-center text-sm text-muted-foreground" data-testid="guide-empty">
              No features match "{query}". Try a different search term.
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
